import { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';
const PROBE_PATH = '/auth/me';
const PROBE_INTERVAL_MS = 3000;

let down = false;
let probing = false;
const listeners = new Set();

export const isBackendDown = () => down;
export const subscribeBackendDown = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

function setDown(value) {
  if (down === value) return;
  down = value;
  listeners.forEach((fn) => fn(value));
}

/**
 * Класифікація відповіді на запит до /api:
 *  - 'down'    — бекенд недоступний: 502/503/504 (проксі) або HTML замість відповіді API
 *                (поки бекенд не готовий, Traefik віддає index.html фронту зі статусом 200);
 *  - 'up'      — звичайна відповідь бекенду (JSON, будь-який статус < 500, у т.ч. 401/404);
 *  - 'neutral' — не показова (напр. 500 — це помилка застосунку, а не недоступність).
 * Тіло не читається.
 */
export function classifyResponse(res) {
  const status = res?.status;
  if (status === 502 || status === 503 || status === 504) return 'down';
  const type = String(res?.headers?.get?.('content-type') || '');
  if (/text\/html/i.test(type)) return 'down';
  if (status >= 500) return 'neutral';
  if (/json/i.test(type)) return 'up';
  return 'neutral';
}

// Чи запит іде на наш бекенд (/api), а не на ZITADEL, НП тощо.
function isApiUrl(input) {
  try {
    const raw = typeof input === 'string' ? input : input?.url;
    if (!raw) return false;
    const url = new URL(raw, window.location.href).href;
    const base = new URL(API_BASE || '/', window.location.href).href.replace(/\/$/, '');
    return url.startsWith(`${base}/`) || url === base;
  } catch {
    return false;
  }
}

const isAbort = (err) => err?.name === 'AbortError';

async function probeOnce(origFetch) {
  try {
    const res = await origFetch(`${API_BASE}${PROBE_PATH}`, { credentials: 'include', cache: 'no-store' });
    return classifyResponse(res) !== 'down';
  } catch {
    return false;
  }
}

// Підозра на недоступність: підтверджуємо окремим запитом (одна повільна відповідь
// не повинна показувати плашку), далі опитуємо, поки бекенд не оговтається.
async function confirmDown(origFetch) {
  if (probing) return;
  probing = true;
  try {
    if (await probeOnce(origFetch)) return;
    setDown(true);
    while (!(await probeOnce(origFetch))) {
      await new Promise((resolve) => setTimeout(resolve, PROBE_INTERVAL_MS));
    }
    setDown(false);
  } finally {
    probing = false;
  }
}

/** Обгортка над fetch: помічає недоступність бекенду за будь-яким запитом до /api. Викликати один раз на старті. */
export function installBackendStatusGuard() {
  if (window.__sparkBackendGuard) return;
  window.__sparkBackendGuard = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (...args) => {
    if (!isApiUrl(args[0])) return orig(...args);
    let res;
    try {
      res = await orig(...args);
    } catch (err) {
      if (!isAbort(err)) confirmDown(orig);
      throw err;
    }
    const kind = classifyResponse(res);
    if (kind === 'down') confirmDown(orig);
    else if (kind === 'up') setDown(false);
    return res;
  };
}

/** React-хук: true, поки бекенд недоступний. */
export function useBackendDown() {
  const [value, setValue] = useState(down);
  useEffect(() => {
    setValue(down);
    return subscribeBackendDown(setValue);
  }, []);
  return value;
}

// Лише для тестів.
export function __resetBackendStatusForTests() {
  down = false;
  probing = false;
  listeners.clear();
  delete window.__sparkBackendGuard;
}
